import React, { useState, useEffect } from 'react';
import './MentorDashboard.css';

function MentorDashboard() {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const [mentorships, setMentorships] = useState({ asMentor: [], asMentee: [] });
  const [selectedMentorship, setSelectedMentorship] = useState(null);
  const [menteeProgress, setMenteeProgress] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [allPMs, setAllPMs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Request Mentorship Form
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  
  // Review Form
  const [reviewProgress, setReviewProgress] = useState('');
  const [reviewStrengths, setReviewStrengths] = useState('');
  const [reviewAreas, setReviewAreas] = useState('');
  const [reviewNextSteps, setReviewNextSteps] = useState('');
  
  useEffect(() => {
    fetchMentorships();
  }, []);
  
  const fetchMentorships = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/mentorships/my`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch mentorships');
      
      const data = await res.json();
      setMentorships({
        asMentor: data.asMentor || [],
        asMentee: data.asMentee || []
      });
    } catch (err) {
      console.error('Error fetching mentorships:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMenteeProgress = async (mentorshipId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const res = await fetch(`${apiUrl}/learning/mentorships/${mentorshipId}/progress`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch progress');
      
      const data = await res.json();
      setMenteeProgress(data);
    } catch (err) {
      console.error('Error fetching mentee progress:', err);
    }
  };
  
  const handleRequestMentorship = async (e) => {
    e.preventDefault();
    if (!selectedMentorId) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/mentorships`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mentorPmId: parseInt(selectedMentorId),
          notes: requestNotes
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to request mentorship');
      }
      
      alert('Mentorship request sent!');
      setShowRequestModal(false);
      setSelectedMentorId('');
      setRequestNotes('');
      fetchMentorships();
    } catch (err) {
      console.error('Error requesting mentorship:', err);
      alert(err.message || 'Failed to send mentorship request');
    }
  };
  
  const handleUpdateStatus = async (mentorshipId, newStatus) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/mentorships/${mentorshipId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to update status');
      }
      
      alert(`Mentorship ${newStatus}`);
      fetchMentorships();
      if (selectedMentorship && selectedMentorship.id === mentorshipId) {
        setSelectedMentorship(null);
        setMenteeProgress(null);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert(err.message || 'Failed to update mentorship status');
    }
  };
  
  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!selectedMentorship || !reviewProgress.trim()) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/mentorships/${selectedMentorship.id}/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          progressSummary: reviewProgress,
          strengths: reviewStrengths,
          areasForImprovement: reviewAreas,
          nextSteps: reviewNextSteps
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to add review');
      }
      
      alert('Progress review added successfully!');
      setShowReviewModal(false);
      setReviewProgress('');
      setReviewStrengths('');
      setReviewAreas('');
      setReviewNextSteps('');
      fetchMenteeProgress(selectedMentorship.id);
    } catch (err) {
      console.error('Error adding review:', err);
      alert(err.message || 'Failed to add progress review');
    }
  };
  
  const fetchAllPMs = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/learning/pms/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error('Failed to fetch PMs');
      
      const data = await res.json();
      setAllPMs(data.pms || []);
    } catch (err) {
      console.error('Error fetching PMs:', err);
    }
  };
  
  const handleSelectMentorship = (mentorship) => {
    setSelectedMentorship(mentorship);
    if (mentorship.status === 'active') {
      fetchMenteeProgress(mentorship.id);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'active': return '#10b981';
      case 'completed': return '#6366f1';
      case 'declined': return '#ef4444';
      default: return '#94a3b8';
    }
  };
  
  return (
    <div className="mentor-dashboard-container">
      <div className="dashboard-header">
        <h1>🤝 Mentorship Dashboard</h1>
        <button 
          onClick={() => {
            setShowRequestModal(true);
            if (allPMs.length === 0) fetchAllPMs();
          }}
          className="request-mentor-btn"
        >
          ➕ Request Mentor
        </button>
      </div>
      
      <div className="dashboard-content">
        <div className="mentorships-columns">
          {/* As Mentor Column */}
          <div className="mentor-column">
            <h2>👨‍🏫 As Mentor ({mentorships.asMentor.length})</h2>
            <div className="mentorships-list">
              {loading ? (
                <div className="loading">Loading...</div>
              ) : mentorships.asMentor.length > 0 ? (
                mentorships.asMentor.map(m => (
                  <div 
                    key={m.id} 
                    className={`mentorship-card ${selectedMentorship?.id === m.id ? 'selected' : ''}`}
                    onClick={() => handleSelectMentorship(m)}
                  >
                    <div className="mentorship-header">
                      <strong>{m.mentee_name}</strong>
                      <span 
                        className="status-badge"
                        style={{ background: `${getStatusColor(m.status)}22`, color: getStatusColor(m.status) }}
                      >
                        {m.status}
                      </span>
                    </div>
                    <p className="mentorship-notes">{m.notes || 'No notes provided'}</p>
                    <div className="mentorship-footer">
                      <span className="mentorship-date">
                        Requested: {new Date(m.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {m.status === 'pending' && (
                      <div className="pending-actions">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateStatus(m.id, 'active');
                          }}
                          className="accept-btn-small"
                        >
                          ✓ Accept
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateStatus(m.id, 'declined');
                          }}
                          className="decline-btn-small"
                        >
                          ✖ Decline
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-mentorships">
                  No mentees yet. Other PMs can request your mentorship.
                </div>
              )}
            </div>
          </div>
          
          {/* As Mentee Column */}
          <div className="mentee-column">
            <h2>👨‍🎓 As Mentee ({mentorships.asMentee.length})</h2>
            <div className="mentorships-list">
              {loading ? (
                <div className="loading">Loading...</div>
              ) : mentorships.asMentee.length > 0 ? (
                mentorships.asMentee.map(m => (
                  <div 
                    key={m.id} 
                    className={`mentorship-card ${selectedMentorship?.id === m.id ? 'selected' : ''}`}
                    onClick={() => handleSelectMentorship(m)}
                  >
                    <div className="mentorship-header">
                      <strong>{m.mentor_name}</strong>
                      <span 
                        className="status-badge"
                        style={{ background: `${getStatusColor(m.status)}22`, color: getStatusColor(m.status) }}
                      >
                        {m.status}
                      </span>
                    </div>
                    <p className="mentorship-notes">{m.notes || 'No notes provided'}</p>
                    <div className="mentorship-footer">
                      <span className="mentorship-date">
                        Requested: {new Date(m.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-mentorships">
                  No mentors yet. Request a mentor to get started!
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Details Panel */}
        {selectedMentorship && (
          <div className="mentorship-details">
            <div className="details-header">
              <div>
                <h2>Mentorship Details</h2>
                <p className="details-subtitle">
                  Mentor: <strong>{selectedMentorship.mentor_name}</strong> | 
                  Mentee: <strong>{selectedMentorship.mentee_name}</strong>
                </p>
              </div>
              <div className="details-actions">
                {selectedMentorship.status === 'active' && (
                  <>
                    <button 
                      onClick={() => setShowReviewModal(true)}
                      className="add-review-btn"
                    >
                      📝 Add Progress Review
                    </button>
                    <button 
                      onClick={() => handleUpdateStatus(selectedMentorship.id, 'completed')}
                      className="complete-btn"
                    >
                      ✓ Mark Complete
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {selectedMentorship.status === 'active' && menteeProgress && (
              <div className="progress-section">
                <h3>📊 Training Progress</h3>
                
                <div className="progress-stats">
                  <div className="stat-card">
                    <div className="stat-value">{menteeProgress.overall_completion_percentage}%</div>
                    <div className="stat-label">Overall Progress</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{menteeProgress.completed_modules}</div>
                    <div className="stat-label">Modules Completed</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{menteeProgress.quizzes_passed}</div>
                    <div className="stat-label">Quizzes Passed</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{menteeProgress.avg_quiz_score}%</div>
                    <div className="stat-label">Avg Quiz Score</div>
                  </div>
                </div>
                
                <div className="modules-progress">
                  <h4>Module Breakdown</h4>
                  <div className="modules-list">
                    {menteeProgress.modules.map(mod => (
                      <div key={mod.module_id} className="module-progress-item">
                        <div className="module-info">
                          <strong>{mod.module_title}</strong>
                          <span className={`module-status ${mod.status}`}>{mod.status}</span>
                        </div>
                        {mod.quiz_score !== null && (
                          <div className="quiz-info">
                            Quiz Score: <strong>{mod.quiz_score}%</strong>
                            {mod.meets_threshold && <span className="pass-badge">✓ Passed</span>}
                          </div>
                        )}
                        {mod.completed_at && (
                          <div className="completion-date">
                            Completed: {new Date(mod.completed_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="reviews-section">
                  <h4>Progress Reviews ({menteeProgress.reviews.length})</h4>
                  {menteeProgress.reviews.length > 0 ? (
                    <div className="reviews-list">
                      {menteeProgress.reviews.map(review => (
                        <div key={review.id} className="review-card">
                          <div className="review-header">
                            <span className="review-date">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="review-content">
                            <div className="review-field">
                              <strong>Progress Summary:</strong>
                              <p>{review.progress_summary}</p>
                            </div>
                            {review.strengths && (
                              <div className="review-field">
                                <strong>Strengths:</strong>
                                <p>{review.strengths}</p>
                              </div>
                            )}
                            {review.areas_for_improvement && (
                              <div className="review-field">
                                <strong>Areas for Improvement:</strong>
                                <p>{review.areas_for_improvement}</p>
                              </div>
                            )}
                            {review.next_steps && (
                              <div className="review-field">
                                <strong>Next Steps:</strong>
                                <p>{review.next_steps}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-reviews">No progress reviews yet.</div>
                  )}
                </div>
              </div>
            )}
            
            {selectedMentorship.status === 'pending' && (
              <div className="pending-notice">
                ⏳ This mentorship request is pending approval.
              </div>
            )}
            
            {selectedMentorship.status === 'completed' && (
              <div className="completed-notice">
                ✓ This mentorship has been completed.
                {selectedMentorship.completed_at && (
                  <span> Completed on {new Date(selectedMentorship.completed_at).toLocaleDateString()}</span>
                )}
              </div>
            )}
            
            {selectedMentorship.status === 'declined' && (
              <div className="declined-notice">
                ✖ This mentorship request was declined.
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Request Mentorship Modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Request Mentorship</h3>
            <button 
              className="modal-close" 
              onClick={() => setShowRequestModal(false)}
            >
              ✖
            </button>
            
            <form onSubmit={handleRequestMentorship} className="request-form">
              <div className="form-group">
                <label>Select Mentor *</label>
                <select
                  value={selectedMentorId}
                  onChange={(e) => setSelectedMentorId(e.target.value)}
                  required
                  className="form-select"
                >
                  <option value="">-- Select a PM --</option>
                  {allPMs.map(pm => (
                    <option key={pm.id} value={pm.id}>
                      {pm.full_name} ({pm.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  placeholder="Why do you want this person as your mentor? What are your goals?"
                  rows="4"
                  className="form-textarea"
                />
              </div>
              
              <button 
                type="submit"
                disabled={!selectedMentorId}
                className="submit-btn"
              >
                Send Request
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Add Progress Review Modal */}
      {showReviewModal && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h3>Add Progress Review</h3>
            <button 
              className="modal-close" 
              onClick={() => setShowReviewModal(false)}
            >
              ✖
            </button>
            
            <form onSubmit={handleAddReview} className="review-form">
              <div className="form-group">
                <label>Progress Summary *</label>
                <textarea
                  value={reviewProgress}
                  onChange={(e) => setReviewProgress(e.target.value)}
                  placeholder="Overall progress since last review..."
                  rows="3"
                  required
                  className="form-textarea"
                />
              </div>
              
              <div className="form-group">
                <label>Strengths</label>
                <textarea
                  value={reviewStrengths}
                  onChange={(e) => setReviewStrengths(e.target.value)}
                  placeholder="What is the mentee doing well?"
                  rows="2"
                  className="form-textarea"
                />
              </div>
              
              <div className="form-group">
                <label>Areas for Improvement</label>
                <textarea
                  value={reviewAreas}
                  onChange={(e) => setReviewAreas(e.target.value)}
                  placeholder="What could be improved?"
                  rows="2"
                  className="form-textarea"
                />
              </div>
              
              <div className="form-group">
                <label>Next Steps</label>
                <textarea
                  value={reviewNextSteps}
                  onChange={(e) => setReviewNextSteps(e.target.value)}
                  placeholder="Recommended actions for next period..."
                  rows="2"
                  className="form-textarea"
                />
              </div>
              
              <button 
                type="submit"
                disabled={!reviewProgress.trim()}
                className="submit-btn"
              >
                Add Review
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MentorDashboard;
